<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
              <span>GRN Details</span>
              <v-btn
                icon="mdi-arrow-left"
                variant="text"
                @click="goBack"
              >
                Back
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text v-if="loading">
              <div class="text-center py-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
            </v-card-text>
            <v-card-text v-else-if="gnrData" id="printable-area">
              <!-- GRN Information -->
              <div class="section-title mb-3">GRN Information</div>
              <v-row>
                <v-col cols="12" md="6">
                  <table class="info-table">
                    <tbody>
                      <tr>
                        <td class="info-label"><strong>GRN No:</strong></td>
                        <td class="info-value">{{ gnrData.gnr_no }}</td>
                      </tr>
                      <tr>
                        <td class="info-label"><strong>GRN Date:</strong></td>
                        <td class="info-value">{{ formatDate(gnrData.gnr_date) }}</td>
                      </tr>
                      <tr>
                        <td class="info-label"><strong>Code:</strong></td>
                        <td class="info-value">{{ gnrData.code }}</td>
                      </tr>
                      <tr>
                        <td class="info-label"><strong>Status:</strong></td>
                        <td class="info-value">
                          <v-chip
                            :color="gnrData.status === 1 ? 'success' : 'grey'"
                            size="small"
                          >
                            {{ gnrData.status === 1 ? 'Posted' : 'Draft' }}
                          </v-chip>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </v-col>
                <v-col cols="12" md="6">
                  <table class="info-table">
                    <tbody>
                      <tr>
                        <td class="info-label"><strong>LC No:</strong></td>
                        <td class="info-value">{{ gnrData.lc?.lc_no || gnrData.lc_id || 'N/A' }}</td>
                      </tr>
                      <tr>
                        <td class="info-label"><strong>Shipment No:</strong></td>
                        <td class="info-value">{{ gnrData.shipment?.shipment_no || gnrData.shipment_id || 'N/A' }}</td>
                      </tr>
                      <tr>
                        <td class="info-label"><strong>Supplier:</strong></td>
                        <td class="info-value">{{ gnrData.supplier?.supplier_name || 'N/A' }}</td>
                      </tr>
                      <tr v-if="gnrData.remarks">
                        <td class="info-label"><strong>Remarks:</strong></td>
                        <td class="info-value">{{ gnrData.remarks }}</td>
                      </tr>
                    </tbody>
                  </table>
                </v-col>
              </v-row>

              <!-- GRN Items -->
              <div class="section-title mb-3 mt-4">GRN Items</div>
              <v-data-table
                :headers="itemHeaders"
                :items="gnrItems"
                :items-per-page="-1"
                hide-default-footer
                class="elevation-1"
              >
                <template v-slot:item.product="{ item }">
                  {{ item.product?.name || '-' }} ({{ item.product?.code || '-' }})
                </template>
                <template v-slot:item.received_qty="{ item }">
                  {{ formatNumber(item.received_qty) }}
                </template>
                <template v-slot:item.accepted_qty="{ item }">
                  {{ formatNumber(item.accepted_qty) }}
                </template>
                <template v-slot:item.rejected_qty="{ item }">
                  {{ formatNumber(item.rejected_qty) }}
                </template>
                <template v-slot:item.rate="{ item }">
                  {{ formatCurrency(item.rate) }}
                </template>
                <template v-slot:item.amount="{ item }">
                  {{ formatCurrency(item.amount) }}
                </template>
              </v-data-table>

              <!-- Summary -->
              <v-card variant="outlined" class="mt-4">
                <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
                  Summary
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="d-flex justify-space-between mb-2">
                        <strong>Total Items:</strong> {{ gnrItems.length }}
                      </div>
                      <div class="d-flex justify-space-between mb-2">
                        <strong>Total Received Qty:</strong> {{ formatNumber(totalReceivedQty) }}
                      </div>
                      <div class="d-flex justify-space-between mb-2">
                        <strong>Total Accepted Qty:</strong> {{ formatNumber(totalAcceptedQty) }}
                      </div>
                      <div class="d-flex justify-space-between mb-2">
                        <strong>Total Rejected Qty:</strong> {{ formatNumber(totalRejectedQty) }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="d-flex justify-space-between mb-2">
                        <strong>Total GRN Value:</strong> {{ formatCurrency(totalGnrValue) }}
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Action Buttons -->
              <div class="text-center mt-6 mb-4 d-print-none">
                <v-btn color="primary" class="mr-2" @click="printPage">
                  <v-icon left>mdi-printer</v-icon> Print
                </v-btn>
                <v-btn color="success" @click="downloadPDF">
                  <v-icon left>mdi-file-pdf-box</v-icon> Download PDF
                </v-btn>
              </div>
            </v-card-text>
            <v-card-text v-else>
              <div class="text-center py-4">
                <p class="text-grey">No GRN data found</p>
                <v-btn color="primary" @click="goBack" class="mt-4">Go Back</v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import formatDateView from "@/plugins/dateFormatView";
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import html2pdf from "html2pdf.js";

export default {
  name: "ViewLcImportGnr",
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const instance = getCurrentInstance();

    const loading = ref(false);
    const gnrData = ref(null);

    const getCurrentCompany = computed(() => {
      return store.getters.getCurrentCompany || {};
    });

    const getFileServerUrl = computed(() => {
      return store.getters.getFileServerUrl || "";
    });

    const itemHeaders = [
      { title: "Product", key: "product", sortable: false },
      { title: "Received Qty", key: "received_qty", align: "end", sortable: false },
      { title: "Accepted Qty", key: "accepted_qty", align: "end", sortable: false },
      { title: "Rejected Qty", key: "rejected_qty", align: "end", sortable: false },
      { title: "Rate", key: "rate", align: "end", sortable: false },
      { title: "Amount", key: "amount", align: "end", sortable: false },
    ];

    const gnrItems = computed(() => {
      if (!gnrData.value) return [];
      // Check for both camelCase and snake_case property names
      const details = gnrData.value.gnrDetails || gnrData.value.gnr_details || [];
      return Array.isArray(details) ? details : [];
    });

    const totalReceivedQty = computed(() => {
      return gnrItems.value.reduce((sum, item) => sum + parseFloat(item.received_qty || 0), 0);
    });

    const totalAcceptedQty = computed(() => {
      return gnrItems.value.reduce((sum, item) => sum + parseFloat(item.accepted_qty || 0), 0);
    });

    const totalRejectedQty = computed(() => {
      return gnrItems.value.reduce((sum, item) => sum + parseFloat(item.rejected_qty || 0), 0);
    });

    const totalGnrValue = computed(() => {
      return gnrItems.value.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    });

    const formatDate = (date) => {
      if (!date) return "";
      return formatDateView(date);
    };

    const formatNumber = (num) => {
      if (!num && num !== 0) return "0.00";
      return Number(num).toFixed(2);
    };

    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return "0.00";
      return Number(amount).toFixed(2);
    };

    const loadGnrData = async () => {
      if (!route.params.id) {
        toast.error("GRN ID is required");
        router.push({ name: "lc-import-gnr-records" });
        return;
      }

      loading.value = true;
      try {
        await store.dispatch("getLcImportGnrById", route.params.id);
        const data = store.getters["getEditingLcImportGnr"];
        
        if (data) {
          // Normalize property names - ensure gnrDetails exists (check both camelCase and snake_case)
          if (!data.gnrDetails && data.gnr_details) {
            data.gnrDetails = data.gnr_details;
          }
          gnrData.value = data;
        } else {
          toast.error("GRN data not found");
          router.push({ name: "lc-import-gnr-records" });
        }
      } catch (error) {
        toast.error("Failed to load GRN data");
        router.push({ name: "lc-import-gnr-records" });
      } finally {
        loading.value = false;
      }
    };

    const goBack = () => {
      router.push({ name: "lc-import-gnr-records" });
    };

    const printPage = () => {
      const printContent = document.getElementById("printable-area").innerHTML;
      const company = getCurrentCompany.value;
      const fileServerUrl = getFileServerUrl.value;
      const printWindow = window.open("", "_blank", `width=${screen.width}, height=${screen.height}, left=0, top=0`);

      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>GRN Details - ${gnrData.value?.gnr_no || ""}</title>
          <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap" rel="stylesheet">
          <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Roboto', sans-serif;
              background: #fff;
              padding: 20px;
              color: #212121;
            }
            .invoice-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 2px solid #eee;
              padding-bottom: 10px;
              margin-bottom: 20px;
            }
            .invoice-header img {
              max-height: 70px;
              max-width: 150px;
            }
            .company-info h4 {
              font-size: 20px;
              font-weight: 600;
              color: #333;
              margin-bottom: 5px;
              text-transform: uppercase;
            }
            .company-info p {
              font-size: 14px;
              color: #555;
              margin: 2px 0;
            }
            .logo-container {
              display: flex;
              align-items: center;
            }
            .section-title {
              font-size: 1.1rem;
              font-weight: 600;
              color: #1976d2;
              margin-bottom: 12px;
              margin-top: 20px;
            }
            .info-table {
              width: 100%;
              border-collapse: collapse;
            }
            .info-table tr {
              border-bottom: 1px solid #e0e0e0;
            }
            .info-table td {
              padding: 8px 12px;
            }
            .info-label {
              width: 40%;
              font-weight: 500;
              color: #616161;
            }
            .info-value {
              width: 60%;
              color: #212121;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            table thead {
              background-color: #f5f5f5;
            }
            table th {
              padding: 12px;
              text-align: left;
              font-weight: 500;
              border-bottom: 2px solid #e0e0e0;
              color: #424242;
            }
            table td {
              padding: 12px;
              border-bottom: 1px solid #e0e0e0;
            }
            table tbody tr:hover {
              background-color: #fafafa;
            }
            .text-end {
              text-align: right;
            }
            .d-flex {
              display: flex;
            }
            .justify-space-between {
              justify-content: space-between;
            }
            .mb-2 {
              margin-bottom: 8px;
            }
            .mb-3 {
              margin-bottom: 12px;
            }
            .mt-4 {
              margin-top: 16px;
            }
            .bg-grey-lighten-4 {
              background-color: #f5f5f5;
            }
            .text-subtitle-1 {
              font-size: 0.875rem;
              font-weight: 500;
            }
            strong {
              font-weight: 500;
            }
            .d-print-none {
              display: none !important;
            }
            /* Vuetify Grid System */
            .row, .v-row {
              display: flex !important;
              flex-wrap: wrap !important;
              margin: 0 -12px !important;
              width: 100% !important;
            }
            .col, .v-col, [class*="col-"], [class*="v-col-"] {
              padding: 0 12px !important;
              flex: 1 1 0%;
              box-sizing: border-box !important;
            }
            .col-12, .v-col-12, [class*="col-12"], [class*="v-col-12"] {
              flex: 0 0 100% !important;
              max-width: 100% !important;
            }
            /* Force two-column layout for medium screens and up */
            @media (min-width: 960px) {
              .col-md-6, .v-col-md-6, [class*="col-md-6"], [class*="v-col-md-6"] {
                flex: 0 0 50% !important;
                max-width: 50% !important;
                width: 50% !important;
              }
            }
            /* Always show two columns in print regardless of screen size */
            @media print {
              .col-md-6, .v-col-md-6, [class*="col-md-6"], [class*="v-col-md-6"] {
                flex: 0 0 50% !important;
                max-width: 50% !important;
                width: 50% !important;
              }
            }
            @media print {
              body {
                padding: 0;
              }
              .d-print-none {
                display: none !important;
              }
              .row, .v-row {
                display: flex !important;
                flex-wrap: wrap !important;
                margin: 0 -12px !important;
              }
              .col-md-6, .v-col-md-6 {
                flex: 0 0 50% !important;
                max-width: 50% !important;
                padding: 0 12px !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="invoice-header">
            <div class="company-info">
              <h4>${company?.name || "Company Name"}</h4>
              <p>${company?.address || ""}</p>
              <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ""}</p>
              <p><i class="mdi mdi-email-outline"></i> ${company?.email || ""}</p>
              ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ""}
            </div>
            <div class="logo-container">
              ${
                company?.logo_thumb
                  ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">`
                  : ""
              }
            </div>
          </div>
          <h3 style="text-align: center; margin-bottom: 20px; font-weight: 600;">GRN DETAILS</h3>
          ${printContent}
        </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.moveTo(0, 0);
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    };

    const downloadPDF = async () => {
      const element = document.getElementById("printable-area");
      if (!element) {
        toast.warning("No GRN data to export");
        return;
      }

      const company = getCurrentCompany.value;
      const fileServerUrl = getFileServerUrl.value;

      // Create a wrapper div with company header
      const wrapper = document.createElement("div");
      wrapper.style.padding = "20px";
      wrapper.style.fontFamily = "'Roboto', sans-serif";
      
      // Add style element for row/col layout
      const style = document.createElement("style");
      style.textContent = `
        .row, .v-row {
          display: flex !important;
          flex-wrap: wrap !important;
          margin: 0 -12px !important;
          width: 100% !important;
        }
        .col, .v-col, [class*="col-"], [class*="v-col-"] {
          padding: 0 12px !important;
          flex: 1 1 0%;
          box-sizing: border-box !important;
        }
        .col-12, .v-col-12, [class*="col-12"], [class*="v-col-12"] {
          flex: 0 0 100% !important;
          max-width: 100% !important;
        }
        .col-md-6, .v-col-md-6, [class*="col-md-6"], [class*="v-col-md-6"] {
          flex: 0 0 50% !important;
          max-width: 50% !important;
          width: 50% !important;
        }
      `;
      wrapper.appendChild(style);

      // Create company header
      const header = document.createElement("div");
      header.style.display = "flex";
      header.style.justifyContent = "space-between";
      header.style.alignItems = "center";
      header.style.borderBottom = "2px solid #eee";
      header.style.paddingBottom = "10px";
      header.style.marginBottom = "20px";

      const companyInfo = document.createElement("div");
      companyInfo.innerHTML = `
        <h4 style="font-size: 20px; font-weight: 600; color: #333; margin-bottom: 5px; text-transform: uppercase;">${company?.name || "Company Name"}</h4>
        <p style="font-size: 14px; color: #555; margin: 2px 0;">${company?.address || ""}</p>
        <p style="font-size: 14px; color: #555; margin: 2px 0;"><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ""}</p>
        <p style="font-size: 14px; color: #555; margin: 2px 0;"><i class="mdi mdi-email-outline"></i> ${company?.email || ""}</p>
        ${company?.website ? `<p style="font-size: 14px; color: #555; margin: 2px 0;"><i class="mdi mdi-web"></i> ${company.website}</p>` : ""}
      `;

      const logoContainer = document.createElement("div");
      if (company?.logo_thumb) {
        const logo = document.createElement("img");
        logo.src = `${fileServerUrl}${company.logo_thumb}`;
        logo.style.maxHeight = "70px";
        logo.style.maxWidth = "150px";
        logo.alt = "Logo";
        logo.crossOrigin = "anonymous"; // Important for CORS
        logoContainer.appendChild(logo);
      }

      header.appendChild(companyInfo);
      header.appendChild(logoContainer);

      // Create title
      const title = document.createElement("h3");
      title.textContent = "GRN DETAILS";
      title.style.textAlign = "center";
      title.style.marginBottom = "20px";
      title.style.fontWeight = "600";

      // Clone the printable area content
      const contentClone = element.cloneNode(true);

      // Append all to wrapper
      wrapper.appendChild(header);
      wrapper.appendChild(title);
      wrapper.appendChild(contentClone);

      // Temporarily add to body for rendering
      wrapper.style.position = "absolute";
      wrapper.style.left = "-9999px";
      document.body.appendChild(wrapper);

      // Wait for images to load - increase timeout for logo
      const logoImg = wrapper.querySelector("img");
      if (logoImg) {
        await new Promise((resolve) => {
          if (logoImg.complete) {
            resolve();
          } else {
            logoImg.onload = resolve;
            logoImg.onerror = resolve; // Continue even if image fails
            setTimeout(resolve, 2000); // Max wait 2 seconds
          }
        });
      } else {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      const opt = {
        margin: [0.5, 0.5],
        filename: `grn-${gnrData.value?.gnr_no || gnrData.value?.code || "details"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff"
        },
        jsPDF: { unit: "cm", format: "a4", orientation: "portrait" },
      };

      try {
        await html2pdf().set(opt).from(wrapper).save();
        toast.success("PDF downloaded successfully");
      } catch (error) {
        toast.error("Failed to generate PDF");
        console.error("PDF generation error:", error);
      } finally {
        document.body.removeChild(wrapper);
      }
    };

    onMounted(() => {
      loadGnrData();
    });

    return {
      loading,
      gnrData,
      gnrItems,
      itemHeaders,
      totalReceivedQty,
      totalAcceptedQty,
      totalRejectedQty,
      totalGnrValue,
      formatDate,
      formatNumber,
      formatCurrency,
      goBack,
      printPage,
      downloadPDF,
    };
  },
};
</script>

<style scoped>
.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 12px;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
}

.info-table tr {
  border-bottom: 1px solid #e0e0e0;
}

.info-table td {
  padding: 8px 12px;
}

.info-label {
  width: 40%;
  font-weight: 500;
  color: #616161;
}

.info-value {
  width: 60%;
  color: #212121;
}
</style>

